import React, {useMemo, useState} from 'react';
import {Modal, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {IconAlertCircle} from '@tabler/icons-react';
import type {
  ProjectAssignmentRuleCreateRequest,
  ProjectAssignmentRuleResponse,
  SourceEntityRecord,
  SourceEntityRequest,
} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {FormPersonnelSelectModalV2} from 'src/app/forms/FormPersonnelSelectModal/FormPersonnelSelectModalV2';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useCreateProjectAssignmentRuleMutation,
  useUpdateProjectAssignmentRuleMutation,
} from 'src/redux/queries/projectAssignmentRule';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AssignmentRuleExceptions} from './AssignmentRuleExceptions';

export interface AssignmentRuleFormModalProps {
  opened: boolean;
  onClose: () => void;
  projectId: number;
  rule?: ProjectAssignmentRuleResponse;
}

// Project is not a selectable audience dimension (circular); vendors are.
const audienceField: IFormFieldV2 = {
  name: 'sourceEntities',
  label: 'Audience',
  type: 'person-select-multiple',
  personOptions: {
    additionalEntityTypes: [EntityTypeEnum.Vendor],
  },
};

export const AssignmentRuleFormModal: React.FC<
  AssignmentRuleFormModalProps
> = ({opened, onClose, projectId, rule}) => {
  const {setAlert} = useAlert();
  const isEdit = rule !== undefined;

  const [name, setName] = useState(rule?.name ?? '');
  const [sourceEntities, setSourceEntities] = useState<SourceEntityRecord[]>(
    rule?.sourceEntities ?? []
  );
  const [selectAllEntitiesType, setSelectAllEntitiesType] = useState<
    EntityTypeEnum | undefined
  >(rule?.selectAllEntityType);
  const [active, setActive] = useState(rule?.active ?? true);

  const [createRule, {isLoading: isCreating}] =
    useCreateProjectAssignmentRuleMutation();
  const [updateRule, {isLoading: isUpdating}] =
    useUpdateProjectAssignmentRuleMutation();
  const isSaving = isCreating || isUpdating;

  const audienceValue: SourceEntityRequest = useMemo(
    () => ({entities: sourceEntities, selectAllEntitiesType}),
    [sourceEntities, selectAllEntitiesType]
  );
  const handleAudienceChange = (_name: string, value: SourceEntityRequest) => {
    // Select-all and concrete entities are mutually exclusive; normalize here once.
    setSourceEntities(
      value.selectAllEntitiesType != null ? [] : value.entities ?? []
    );
    setSelectAllEntitiesType(value.selectAllEntitiesType);
  };

  const resetAndClose = () => {
    onClose();
  };

  const handleSave = async () => {
    const request: ProjectAssignmentRuleCreateRequest = {
      projectId,
      name: name.trim() || undefined,
      active,
      selectAllEntityType: selectAllEntitiesType,
      sourceEntities,
    };

    try {
      if (isEdit) {
        await updateRule({...request, id: rule.id}).unwrap();
        setAlert('Assignment rule updated.', 'success');
      } else {
        await createRule(request).unwrap();
        setAlert('Assignment rule created.', 'success');
      }
      resetAndClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const canSave = selectAllEntitiesType != null || sourceEntities.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={resetAndClose}
      title={isEdit ? 'Edit Assignment Rule' : 'New Assignment Rule'}
      centered
      size="xl">
      <Alert icon={<IconAlertCircle size={16} />} color="blue" mb="md">
        This is a <strong>live</strong> rule: new matching people are assigned
        to this project automatically (within ~12 hours), and removing someone
        from the audience un-assigns them.
      </Alert>

      <Stack gap="md">
        <TextInput
          label="Rule Name (optional)"
          placeholder="e.g. Operations Team"
          value={name}
          onChange={e => setName(e.currentTarget.value)}
        />

        <FormPersonnelSelectModalV2
          field={audienceField}
          value={audienceValue}
          onChange={handleAudienceChange}
        />

        <Switch
          label="Active"
          description="Inactive rules stop assigning new people but keep existing exceptions."
          checked={active}
          onChange={e => setActive(e.currentTarget.checked)}
        />

        {!canSave && (
          <Alert color="yellow" py="xs">
            Select at least one audience dimension before saving.
          </Alert>
        )}

        {isEdit && (
          <>
            <Divider label="Exceptions" labelPosition="left" />
            <AssignmentRuleExceptions ruleId={rule.id} canEdit={true} />
          </>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={resetAndClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSave || isSaving}
            loading={isSaving}
            onClick={handleSave}>
            {isEdit ? 'Save Changes' : 'Create Rule'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
